import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './PublicTaskSearch.css';
import '../Tasks/Task.css';
import { 
  IPublicTask, 
  IPublicTaskFilters 
} from '../../../interfaces/IPublicTask';
import useApiRequests from '../../../hooks/useApiRequests';

const PublicTaskSearch: React.FC = () => {
  const { searchPublicApprovedTasks, loading } = useApiRequests();
  const [tasks, setTasks] = useState<IPublicTask[]>([]);
  const [error, setError] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<IPublicTask | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('publicTaskSearchTheme');
    return true;
  });

  const pageSize = 10;

  const availableStatuses = [
    { value: '', label: 'Todos os Status' },
    { value: 'Backlog', label: 'Backlog' },
    { value: 'To Do', label: 'To Do' },
    { value: 'Doing', label: 'Doing' },
    { value: 'PR Aguardando', label: 'PR Aguardando' },
    { value: 'PR Aprovado', label: 'PR Aprovado' },
    { value: 'Publicado em Homologação', label: 'Publicado em Homologação' },
    { value: 'Testing', label: 'Testing' },
    { value: 'Test Finished', label: 'Test Finished' },
    { value: 'Publicado', label: 'Publicado' },
  ];

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('publicTaskSearchTheme', newTheme ? 'dark' : 'light');
  };

  const handleSearch = async (page: number = 1) => {
    try {
      setError('');

      const filters: IPublicTaskFilters = {
        searchText: searchText.trim() || undefined,
        status: selectedStatus || undefined,
        pageNumber: page,
        pageSize: pageSize,
      };

      const response = await searchPublicApprovedTasks(filters);

      if (response.success) {
        setTasks(response.data.tasks);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
        setCurrentPage(page);
      } else {
        setError(response.msg || 'Erro ao buscar tarefas');
        setTasks([]);
      }
    } catch (err) {
      setError('Erro ao buscar tarefas. Tente novamente mais tarde.');
      setTasks([]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(1);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const truncateDescription = (description?: string, maxLength: number = 300): string => {
    if (!description) return '';
    const cleanText = description.replace(/\*\*/g, '').replace(/\n/g, ' ').trim();
    if (cleanText.length <= maxLength) return cleanText;
    return cleanText.substring(0, maxLength) + '...';
  };

  const handleTaskClick = (task: IPublicTask) => {
    setSelectedTask(task);
  };

  const closeModal = () => {
    setSelectedTask(null);
  };

  // useEffect(() => {
  //   handleSearch(1);
  // }, []);

  return (
    <div className={`public-task-search ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <div className="public-search-header">
        <div className="header-content">
          <div>
            <h1>Consulta Pública de Tarefas Aprovadas</h1>
            <p>Consulte o status de tarefas já aprovadas e em desenvolvimento</p>
          </div>
          <button 
            className="theme-toggle-btn" 
            onClick={toggleTheme}
            aria-label="Alternar tema"
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      <div className="search-container">
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Busque por título, ticket, categoria ou descrição..."
              className="search-input"
              disabled={loading}
            />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="status-select"
              disabled={loading}
            >
              {availableStatuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <button 
              onClick={() => handleSearch(1)} 
              className="search-button"
              disabled={loading}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {!loading && tasks.length === 0 && !error && (
          <div className="empty-state">
            <p>Nenhuma tarefa encontrada.</p>
            <p>Tente ajustar os filtros de busca.</p>
          </div>
        )}

        {tasks.length > 0 && (
          <>
            <div className="results-info">
              Encontradas {totalCount} tarefa(s)
            </div>

            <div className="tasks-list">
              {tasks.map((task) => (
                <div 
                  key={task.taskId} 
                  className="task-card"
                  onClick={() => handleTaskClick(task)}
                >
                  <div className="task-header">
                    <h3>{task.title}</h3>
                    <span className="task-status">STATUS: {task.status}</span>
                  </div>
                  <div className="task-info">
                    <span className="task-ticket">
                      <strong>Ticket:</strong> {task.ticket || '-'}
                    </span>
                    <span className="task-category">
                      <strong>Categoria:</strong> {task.category || '-'}
                    </span>
                    <span className="task-date">
                      <strong>Criada em:</strong> {formatDate(task.createdDate)}
                    </span>
                  </div>
                  {task.description && (
                    <div className="task-description-preview">
                      <strong>Descrição: </strong>
                      {truncateDescription(task.description, 300)}
                    </div>
                  )}
                  {task.priority && (
                    <div className="task-priority">
                      Prioridade: {task.priority}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handleSearch(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="pagination-button"
                >
                  Anterior
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => handleSearch(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="pagination-button"
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedTask && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-section">
                <div className="task-status-badge">
                  📍 {selectedTask.status}
                </div>
                <h2>{selectedTask.title}</h2>
              </div>
              <button className="modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {/* Informações Gerais */}
              <div className="detail-section">
                <h3 className="section-title">Informações Gerais</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Ticket:</span>
                    <span className="detail-value">{selectedTask.ticket || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Categoria:</span>
                    <span className="detail-value">{selectedTask.category || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Prioridade:</span>
                    <span className="detail-value priority">{selectedTask.priority || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Complexidade:</span>
                    <span className="detail-value">{selectedTask.complexity || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Versão:</span>
                    <span className="detail-value">{selectedTask.version || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="detail-section">
                <h3 className="section-title">Timeline</h3>
                <div className="timeline">
                  <div className="timeline-item">
                    <span className="timeline-label">Criada em:</span>
                    <span className="timeline-value">{formatDate(selectedTask.createdDate)}</span>
                  </div>
                  {selectedTask.approvedDate && (
                    <div className="timeline-item">
                      <span className="timeline-label">Aprovada em:</span>
                      <span className="timeline-value">{formatDate(selectedTask.approvedDate)}</span>
                    </div>
                  )}
                  {selectedTask.finishDate && (
                    <div className="timeline-item">
                      <span className="timeline-label">Concluída em:</span>
                      <span className="timeline-value">{formatDate(selectedTask.finishDate)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Badges */}
              {selectedTask.isCustomization && (
                <div className="detail-section">
                  <div className="badges-container">
                    <span className="badge-customization">🔧 Customização</span>
                  </div>
                </div>
              )}

              {/* Descrição */}
              {selectedTask.description && (
                <div className="detail-section">
                  <h3 className="section-title">Descrição</h3>
                  <div className="task-description-content">
                    <ReactMarkdown className="markdown">
                      {selectedTask.description.replace(/\n/g, "  \n")}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicTaskSearch;

